import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Plus, 
  Search,
  Building2,
  FileText,
  CheckCircle2,
  Eye,
  X,
  Target,
  DollarSign,
  MapPin,
  Globe,
  Phone,
  Briefcase,
  Download,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Industry badge color mapping
const getIndustryBadgeStyle = (industry) => {
  const styles = {
    'WOSB': 'bg-rose-100 text-rose-700 border-rose-200',
    'SBA 8(a)': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    '8(a) Small Business': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'HUBZone': 'bg-blue-100 text-blue-700 border-blue-200',
    'SDVOSB': 'bg-purple-100 text-purple-700 border-purple-200',
    'Small Business': 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return styles[industry] || 'bg-slate-100 text-slate-700 border-slate-200';
};

export default function ConsultantClientDirectory() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { user, authToken, getAuthHeaders } = useAuth();
  
  // Get consultant org ID from authenticated user
  const consultantOrgId = user?.organizationId || 'c0000000-0000-0000-0000-000000000001';
  
  // API State
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // Edit Client (v3.10.3)
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editClientForm, setEditClientForm] = useState(null); // populated on open

  // UEI duplicate-link confirmation (Option B flow)
  const [ueiConflict, setUeiConflict] = useState(null); // { id, name, uei, type } | null
  const [isLinking, setIsLinking] = useState(false);

  // Portfolio stats for the 3 cards at top (v3.10.4 — was hardcoded `0`/`$0`).
  const [portfolioStats, setPortfolioStats] = useState({ active: 0, pipeline_value: 0 });
  
  // New Client Form State
  const [newClientForm, setNewClientForm] = useState({
    name: '',
    uei: '',
    cage_code: '',
    business_size: '',
    primary_naics: '',
    industry_sector: '',
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    phone: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_title: ''
  });

  // Fetch clients from API
  const fetchClients = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/clients?consultant_org_id=${consultantOrgId}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch clients');
      const data = await response.json();
      setClients(data.items || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
      toast.error('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  };

  // Load clients on mount
  useEffect(() => {
    fetchClients();
    fetchPortfolioStats();
  }, []);

  // v3.10.4: real portfolio aggregates replacing the hardcoded `0`/`$0`.
  // `/api/pursuits/stats` already respects consultant access rules, so
  // without any org_id filter it returns the roll-up across ALL pursuits
  // the consultant has access to — which is exactly what this card wants.
  const fetchPortfolioStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/stats`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setPortfolioStats({
        active: data.active ?? 0,
        pipeline_value: data.pipeline_value ?? 0,
      });
    } catch (err) {
      console.error('portfolio stats fetch failed:', err);
    }
  };

  const resetAddForm = () => {
    setNewClientForm({
      name: '', uei: '', cage_code: '', business_size: '', primary_naics: '',
      industry_sector: '', address_line1: '', city: '', state: '', zip_code: '',
      website: '', phone: '', admin_email: '', admin_first_name: '', admin_last_name: '', admin_title: ''
    });
  };

  // Create new client — with UEI dedup (Option B)
  const handleCreateClient = async () => {
    setIsCreating(true);
    try {
      const payload = {
        name: newClientForm.name,
        uei: newClientForm.uei ? newClientForm.uei.trim().toUpperCase() : null,
        cage_code: newClientForm.cage_code || null,
        business_size: newClientForm.business_size || null,
        primary_naics: newClientForm.primary_naics || null,
        industry_sector: newClientForm.industry_sector || null,
        address_line1: newClientForm.address_line1 || null,
        city: newClientForm.city || null,
        state: newClientForm.state || null,
        zip_code: newClientForm.zip_code || null,
        website: newClientForm.website || null,
        phone: newClientForm.phone || null,
      };

      if (newClientForm.admin_email) {
        payload.admin = {
          email: newClientForm.admin_email,
          first_name: newClientForm.admin_first_name || 'Admin',
          last_name: newClientForm.admin_last_name || 'User',
          title: newClientForm.admin_title || null
        };
      }

      const response = await fetch(
        `${API_URL}/api/clients?consultant_org_id=${consultantOrgId}`,
        { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
      );

      if (response.status === 409) {
        const errData = await response.json();
        const detail = errData.detail || {};
        if (detail.error === 'UEI_EXISTS') {
          // Surface the confirmation dialog — keep the add modal open behind it
          setUeiConflict(detail.existing_org);
          return;
        }
        if (detail.error === 'ALREADY_LINKED') {
          toast.error(detail.message || 'Already linked to this organisation');
          return;
        }
        throw new Error(detail.message || 'Conflict');
      }

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(
          typeof errData.detail === 'string' ? errData.detail :
          errData.detail?.message || 'Failed to create client'
        );
      }

      const newClient = await response.json();
      toast.success(`Client "${newClient.name}" created successfully!`);
      resetAddForm();
      setIsAddClientModalOpen(false);
      fetchClients();
    } catch (err) {
      console.error('Error creating client:', err);
      toast.error(err.message || 'Failed to create client');
    } finally {
      setIsCreating(false);
    }
  };

  // Confirm link to existing org (after UEI conflict prompt)
  const handleLinkExisting = async () => {
    if (!ueiConflict) return;
    setIsLinking(true);
    try {
      const response = await fetch(
        `${API_URL}/api/clients/link-existing?consultant_org_id=${consultantOrgId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ org_id: ueiConflict.id }),
        }
      );
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(
          typeof errData.detail === 'string' ? errData.detail :
          errData.detail?.message || 'Failed to link organisation'
        );
      }
      const linked = await response.json();
      toast.success(`Linked to existing organisation: ${linked.name}`);
      setUeiConflict(null);
      resetAddForm();
      setIsAddClientModalOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.message || 'Failed to link organisation');
    } finally {
      setIsLinking(false);
    }
  };

  // ── Edit Client (v3.10.3) ──
  // Opens the Edit modal pre-populated from the currently selected
  // client. Backend `PUT /api/clients/{id}` accepts the same 13 fields
  // the Add form uses (minus admin-invite fields, which aren't
  // editable post-creation).
  const openEditModal = (client) => {
    if (!client) return;
    setEditClientForm({
      id: client.id,
      name: client.name || '',
      uei: client.uei || '',
      cage_code: client.cage_code || '',
      business_size: client.business_size || '',
      primary_naics: client.primary_naics || '',
      industry_sector: client.industry_sector || '',
      address_line1: client.address_line1 || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      website: client.website || '',
      phone: client.phone || '',
    });
    setIsEditClientModalOpen(true);
  };

  const updateEditClientForm = (field, value) => {
    setEditClientForm(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSaveClient = async () => {
    if (!editClientForm?.id) return;
    if (!editClientForm.name || editClientForm.name.trim().length < 2) {
      toast.error('Client name is required');
      return;
    }
    setIsSaving(true);
    try {
      // Build patch: only send non-null strings (empty → null on server)
      const payload = {
        name: editClientForm.name.trim(),
        uei: editClientForm.uei || null,
        cage_code: editClientForm.cage_code || null,
        business_size: editClientForm.business_size || null,
        primary_naics: editClientForm.primary_naics || null,
        industry_sector: editClientForm.industry_sector || null,
        address_line1: editClientForm.address_line1 || null,
        city: editClientForm.city || null,
        state: editClientForm.state || null,
        zip_code: editClientForm.zip_code || null,
        website: editClientForm.website || null,
        phone: editClientForm.phone || null,
      };

      const response = await fetch(
        `${API_URL}/api/clients/${editClientForm.id}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to update client');
      }

      const updated = await response.json();
      toast.success(`Client "${updated.name}" updated`);
      setIsEditClientModalOpen(false);
      setEditClientForm(null);

      // Refresh list + keep the side panel in sync with the fresh values.
      fetchClients();
      setSelectedClient(prev => (prev && prev.id === updated.id
        ? { ...prev, ...updated }
        : prev));
    } catch (err) {
      console.error('Error updating client:', err);
      toast.error(err.message || 'Failed to update client');
    } finally {
      setIsSaving(false);
    }
  };

  const canSaveClient = (editClientForm?.name || '').trim().length >= 2;

  // Calculate stats
  // v3.10.4: fed from `/api/pursuits/stats` (fetchPortfolioStats above).
  // Values respect consultant access filters on the server.
  const formatCurrencyCompact = (v) => {
    const n = Number(v) || 0;
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toLocaleString()}`;
  };
  const totalActivePursuits = portfolioStats.active;
  const totalPipelineValue = formatCurrencyCompact(portfolioStats.pipeline_value);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!filterQuery) return clients;
    const query = filterQuery.toLowerCase();
    return clients.filter(client => 
      client.name?.toLowerCase().includes(query) ||
      (client.city && client.state && `${client.city}, ${client.state}`.toLowerCase().includes(query)) ||
      client.business_size?.toLowerCase().includes(query) ||
      client.uei?.toLowerCase().includes(query)
    );
  }, [filterQuery, clients]);

  const handleClientClick = (client) => {
    setSelectedClient(selectedClient?.id === client.id ? null : client);
  };

  const handleViewProfile = (clientId) => {
    navigate(`/${workspaceId}/clients/${clientId}`);
  };

  const updateNewClientForm = (field, value) => {
    setNewClientForm(prev => ({ ...prev, [field]: value }));
  };

  const canCreateClient = newClientForm.name.length >= 2;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-blue-50/30">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultant-clients-shell" className="flex min-h-screen bg-slate-50">
      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-300", selectedClient ? "mr-[400px]" : "")}>
        {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
        <div className="bg-white border-b border-slate-200 px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm text-slate-500 mb-1">Consultant Portal</p>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Client Directory
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              <Button variant="outline" className="border-slate-200" data-testid="export-portfolio-btn">
                <Download className="w-4 h-4 mr-2" />
                Export Portfolio
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setIsAddClientModalOpen(true)}
                data-testid="add-client-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="max-w-7xl mx-auto space-y-6">
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {clients.length}
                    </p>
                    <p className="text-sm text-slate-500">Active Clients</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {totalActivePursuits}
                    </p>
                    <p className="text-sm text-slate-500">Total Active Pursuits</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {totalPipelineValue}
                    </p>
                    <p className="text-sm text-slate-500">Pipeline Value</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client List Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                CLIENT LIST ({clients.length})
              </h2>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchClients}
                  className="border-slate-200"
                  data-testid="refresh-clients-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search clients..."
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    className="pl-10 w-64 border-slate-200 bg-white"
                    data-testid="client-filter-search"
                  />
                </div>
              </div>
            </div>

            {/* Client Cards - Real Data */}
            <div className="space-y-4">
              {filteredClients.length === 0 ? (
                <Card className="border-slate-200 bg-white shadow-sm">
                  <CardContent className="p-12 text-center">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Clients Yet</h3>
                    <p className="text-slate-500 mb-4">Add your first client to get started</p>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => setIsAddClientModalOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Client
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                filteredClients.map((client) => {
                  const isSelected = selectedClient?.id === client.id;
                  const location = client.city && client.state 
                    ? `${client.city}, ${client.state}` 
                    : client.city || client.state || 'Location not set';
                  
                  return (
                    <Card 
                      key={client.id} 
                      className={cn(
                        "border bg-white shadow-sm cursor-pointer transition-all duration-200",
                        isSelected ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"
                      )}
                      onClick={() => handleClientClick(client)}
                      data-testid={`client-card-${client.id}`}
                    >
                      <CardContent className="p-6">
                        {/* Client Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-100">
                              <Building2 className="w-7 h-7 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {client.name}
                              </h3>
                              <div className="flex items-center gap-1 text-sm text-slate-500">
                                <MapPin className="w-3.5 h-3.5" />
                                {location}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {client.business_size && (
                              <Badge className={cn("border font-medium", getIndustryBadgeStyle(client.business_size))}>
                                {client.business_size}
                              </Badge>
                            )}
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                              {client.link_status}
                            </Badge>
                          </div>
                        </div>

                        {/* UEI & CAGE Codes */}
                        <div className="flex items-center gap-4 mb-4">
                          {client.uei && (
                            <div className="px-3 py-1.5 bg-slate-100 rounded-lg">
                              <span className="text-xs text-slate-500">UEI:</span>
                              <span className="text-xs font-mono font-medium text-slate-700 ml-1">{client.uei}</span>
                            </div>
                          )}
                          {client.cage_code && (
                            <div className="px-3 py-1.5 bg-slate-100 rounded-lg">
                              <span className="text-xs text-slate-500">CAGE:</span>
                              <span className="text-xs font-mono font-medium text-slate-700 ml-1">{client.cage_code}</span>
                            </div>
                          )}
                          {client.primary_naics && (
                            <div className="px-3 py-1.5 bg-slate-100 rounded-lg">
                              <span className="text-xs text-slate-500">NAICS:</span>
                              <span className="text-xs font-mono font-medium text-slate-700 ml-1">{client.primary_naics}</span>
                            </div>
                          )}
                        </div>

                        {/* Bottom Row */}
                        <div className="flex items-end justify-between">
                          <div>
                            {client.industry_sector && (
                              <p className="text-sm text-slate-600">
                                <span className="font-medium">Industry:</span> {client.industry_sector}
                              </p>
                            )}
                            {client.linked_at && (
                              <p className="text-xs text-slate-400 mt-1">
                                Linked: {new Date(client.linked_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-blue-200 text-blue-600 hover:bg-blue-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(client.id);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Profile
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Quick View Panel - Slides in from right */}
      {selectedClient && (
        <div className="fixed right-0 top-0 h-full w-[400px] bg-blue-50 border-l border-slate-200 shadow-xl z-40 overflow-y-auto" data-testid="quick-view-panel">
          {/* Panel Header */}
          <div className="bg-blue-100 px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Client Details</p>
              <button 
                onClick={() => setSelectedClient(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-200 transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {selectedClient.name}
                </h3>
                <p className="text-sm text-slate-600">
                  {selectedClient.city && selectedClient.state 
                    ? `${selectedClient.city}, ${selectedClient.state}` 
                    : 'Location not set'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Business Info */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Business Information</h4>
              <div className="space-y-2">
                {selectedClient.uei && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-500">UEI</span>
                    <span className="font-mono font-medium text-slate-900">{selectedClient.uei}</span>
                  </div>
                )}
                {selectedClient.cage_code && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-500">CAGE Code</span>
                    <span className="font-mono font-medium text-slate-900">{selectedClient.cage_code}</span>
                  </div>
                )}
                {selectedClient.primary_naics && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-500">Primary NAICS</span>
                    <span className="font-mono font-medium text-slate-900">{selectedClient.primary_naics}</span>
                  </div>
                )}
                {selectedClient.business_size && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-500">Business Size</span>
                    <Badge className={getIndustryBadgeStyle(selectedClient.business_size)}>
                      {selectedClient.business_size}
                    </Badge>
                  </div>
                )}
                {selectedClient.industry_sector && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-500">Industry</span>
                    <span className="font-medium text-slate-900">{selectedClient.industry_sector}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            {(selectedClient.website || selectedClient.phone) && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Contact</h4>
                <div className="space-y-2">
                  {selectedClient.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={selectedClient.website.startsWith('http') ? selectedClient.website : `https://${selectedClient.website}`} 
                         target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 hover:underline">
                        {selectedClient.website}
                      </a>
                    </div>
                  )}
                  {selectedClient.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{selectedClient.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address */}
            {selectedClient.address_line1 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Address</h4>
                <p className="text-sm text-slate-600">
                  {selectedClient.address_line1}<br/>
                  {selectedClient.city && `${selectedClient.city}, `}
                  {selectedClient.state} {selectedClient.zip_code}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleViewProfile(selectedClient.id)}
                data-testid="view-full-profile-btn"
              >
                View Full Profile
              </Button>
              <Button
                variant="outline"
                className="w-full border-slate-200"
                onClick={() => openEditModal(selectedClient)}
                data-testid="edit-client-details-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Edit Client Details
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      <Dialog open={isAddClientModalOpen} onOpenChange={setIsAddClientModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="add-client-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Building2 className="w-5 h-5 text-blue-600" />
              Add New Client
            </DialogTitle>
            <DialogDescription>
              Create a new client organization. This will be linked to your consultancy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Organization Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900 text-sm">Organization Information</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="client-name">Company Name *</Label>
                  <Input
                    id="client-name"
                    placeholder="Acme Defense Systems"
                    value={newClientForm.name}
                    onChange={(e) => updateNewClientForm('name', e.target.value)}
                    className="mt-1"
                    data-testid="new-client-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="client-uei">UEI</Label>
                  <Input
                    id="client-uei"
                    placeholder="H93L94K2M1P5"
                    value={newClientForm.uei}
                    onChange={(e) => updateNewClientForm('uei', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={12}
                    data-testid="new-client-uei"
                  />
                </div>
                
                <div>
                  <Label htmlFor="client-cage">CAGE Code</Label>
                  <Input
                    id="client-cage"
                    placeholder="7Z9X2"
                    value={newClientForm.cage_code}
                    onChange={(e) => updateNewClientForm('cage_code', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={5}
                    data-testid="new-client-cage"
                  />
                </div>
                
                <div>
                  <Label htmlFor="client-naics">Primary NAICS</Label>
                  <Input
                    id="client-naics"
                    placeholder="541512"
                    value={newClientForm.primary_naics}
                    onChange={(e) => updateNewClientForm('primary_naics', e.target.value)}
                    className="mt-1 font-mono"
                    maxLength={6}
                    data-testid="new-client-naics"
                  />
                </div>
                
                <div>
                  <Label htmlFor="client-size">Business Size</Label>
                  <Input
                    id="client-size"
                    placeholder="8(a) Small Business"
                    value={newClientForm.business_size}
                    onChange={(e) => updateNewClientForm('business_size', e.target.value)}
                    className="mt-1"
                    data-testid="new-client-size"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="client-industry">Industry Sector</Label>
                  <Input
                    id="client-industry"
                    placeholder="Information Technology"
                    value={newClientForm.industry_sector}
                    onChange={(e) => updateNewClientForm('industry_sector', e.target.value)}
                    className="mt-1"
                    data-testid="new-client-industry"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Address */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900 text-sm">Location</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="client-address">Street Address</Label>
                  <Input
                    id="client-address"
                    placeholder="123 Federal Way"
                    value={newClientForm.address_line1}
                    onChange={(e) => updateNewClientForm('address_line1', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="client-city">City</Label>
                  <Input
                    id="client-city"
                    placeholder="Reston"
                    value={newClientForm.city}
                    onChange={(e) => updateNewClientForm('city', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="client-state">State</Label>
                    <Input
                      id="client-state"
                      placeholder="VA"
                      value={newClientForm.state}
                      onChange={(e) => updateNewClientForm('state', e.target.value)}
                      className="mt-1"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client-zip">ZIP</Label>
                    <Input
                      id="client-zip"
                      placeholder="20191"
                      value={newClientForm.zip_code}
                      onChange={(e) => updateNewClientForm('zip_code', e.target.value)}
                      className="mt-1"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Primary Admin (Optional) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-900 text-sm">Primary Admin (Optional)</h4>
                <span className="text-xs text-slate-500">An invitation will be sent</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="sarah@acmedefense.com"
                    value={newClientForm.admin_email}
                    onChange={(e) => updateNewClientForm('admin_email', e.target.value)}
                    className="mt-1"
                    data-testid="new-client-admin-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="admin-first">First Name</Label>
                  <Input
                    id="admin-first"
                    placeholder="Sarah"
                    value={newClientForm.admin_first_name}
                    onChange={(e) => updateNewClientForm('admin_first_name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="admin-last">Last Name</Label>
                  <Input
                    id="admin-last"
                    placeholder="Johnson"
                    value={newClientForm.admin_last_name}
                    onChange={(e) => updateNewClientForm('admin_last_name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="admin-title">Title</Label>
                  <Input
                    id="admin-title"
                    placeholder="VP of Business Development"
                    value={newClientForm.admin_title}
                    onChange={(e) => updateNewClientForm('admin_title', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddClientModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!canCreateClient || isCreating}
              onClick={handleCreateClient}
              data-testid="create-client-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Client
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UEI duplicate-link confirmation dialog */}
      <Dialog open={!!ueiConflict} onOpenChange={(open) => { if (!open) setUeiConflict(null); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="uei-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Building2 className="w-5 h-5 text-amber-500" />
              Organisation already exists in FedBD
            </DialogTitle>
            <DialogDescription className="pt-2">
              <span className="font-semibold text-slate-800">{ueiConflict?.name}</span>
              {ueiConflict?.uei && (
                <span className="ml-1 font-mono text-xs text-slate-500">(UEI: {ueiConflict.uei})</span>
              )}
              {' '}already has a record in FedBD
              {ueiConflict?.type === 'CLIENT' ? ' as a client organisation' : ueiConflict?.type ? ` (${ueiConflict.type})` : ''}.
              <br /><br />
              Would you like to <strong>link your account to their existing record</strong> instead of creating a duplicate?
              Any pursuits, documents, and opportunity data already in their account will be accessible to you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setUeiConflict(null)}
              className="w-full sm:w-auto"
            >
              Cancel — keep editing
            </Button>
            <Button
              onClick={handleLinkExisting}
              disabled={isLinking}
              className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="uei-conflict-link-btn"
            >
              {isLinking
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Linking…</>
                : <><CheckCircle2 className="w-4 h-4 mr-2" />Link to existing record</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal (v3.10.3) */}
      <Dialog open={isEditClientModalOpen} onOpenChange={setIsEditClientModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="edit-client-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="w-5 h-5 text-blue-600" />
              Edit Client Details
            </DialogTitle>
            <DialogDescription>
              Update organization details. Changes are saved to the client's
              profile and visible across all pursuits linked to this client.
            </DialogDescription>
          </DialogHeader>

          {editClientForm && (
            <div className="space-y-6 py-4">
              {/* Organization Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 text-sm">Organization Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="edit-client-name">Company Name *</Label>
                    <Input
                      id="edit-client-name"
                      placeholder="Acme Defense Systems"
                      value={editClientForm.name}
                      onChange={(e) => updateEditClientForm('name', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-uei">UEI</Label>
                    <Input
                      id="edit-client-uei"
                      placeholder="H93L94K2M1P5"
                      value={editClientForm.uei}
                      onChange={(e) => updateEditClientForm('uei', e.target.value)}
                      className="mt-1 font-mono"
                      maxLength={12}
                      data-testid="edit-client-uei"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-cage">CAGE Code</Label>
                    <Input
                      id="edit-client-cage"
                      placeholder="7Z9X2"
                      value={editClientForm.cage_code}
                      onChange={(e) => updateEditClientForm('cage_code', e.target.value)}
                      className="mt-1 font-mono"
                      maxLength={5}
                      data-testid="edit-client-cage"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-naics">Primary NAICS</Label>
                    <Input
                      id="edit-client-naics"
                      placeholder="541512"
                      value={editClientForm.primary_naics}
                      onChange={(e) => updateEditClientForm('primary_naics', e.target.value)}
                      className="mt-1 font-mono"
                      maxLength={6}
                      data-testid="edit-client-naics"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-size">Business Size</Label>
                    <Input
                      id="edit-client-size"
                      placeholder="8(a) Small Business"
                      value={editClientForm.business_size}
                      onChange={(e) => updateEditClientForm('business_size', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-size"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit-client-industry">Industry Sector</Label>
                    <Input
                      id="edit-client-industry"
                      placeholder="Information Technology"
                      value={editClientForm.industry_sector}
                      onChange={(e) => updateEditClientForm('industry_sector', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-industry"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Contact */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 text-sm">Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-client-website">Website</Label>
                    <Input
                      id="edit-client-website"
                      placeholder="https://acmedefense.com"
                      value={editClientForm.website}
                      onChange={(e) => updateEditClientForm('website', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-website"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-phone">Phone</Label>
                    <Input
                      id="edit-client-phone"
                      placeholder="703-555-0100"
                      value={editClientForm.phone}
                      onChange={(e) => updateEditClientForm('phone', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-phone"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Address */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 text-sm">Location</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="edit-client-address">Street Address</Label>
                    <Input
                      id="edit-client-address"
                      placeholder="123 Federal Way"
                      value={editClientForm.address_line1}
                      onChange={(e) => updateEditClientForm('address_line1', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-client-city">City</Label>
                    <Input
                      id="edit-client-city"
                      placeholder="Reston"
                      value={editClientForm.city}
                      onChange={(e) => updateEditClientForm('city', e.target.value)}
                      className="mt-1"
                      data-testid="edit-client-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="edit-client-state">State</Label>
                      <Input
                        id="edit-client-state"
                        placeholder="VA"
                        value={editClientForm.state}
                        onChange={(e) => updateEditClientForm('state', e.target.value)}
                        className="mt-1"
                        maxLength={2}
                        data-testid="edit-client-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-client-zip">ZIP</Label>
                      <Input
                        id="edit-client-zip"
                        placeholder="20191"
                        value={editClientForm.zip_code}
                        onChange={(e) => updateEditClientForm('zip_code', e.target.value)}
                        className="mt-1"
                        maxLength={10}
                        data-testid="edit-client-zip"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setIsEditClientModalOpen(false); setEditClientForm(null); }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!canSaveClient || isSaving}
              onClick={handleSaveClient}
              data-testid="save-client-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
